import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { TODO_NOTIFICATION_TYPES } from 'NotificationsApp/ts/features/notifications-side-menu/constants';
import { Notification } from 'NotificationsApp/ts/interfaces/notifications.interface';

import { NotificationsService } from 'NotificationsApp/ts/services/notifications.service';

@Component({
    standalone: false,
    selector: 'tr[ycn-notifications-menu-list-item]',
    templateUrl: './notifications-menu-list-item.template.html',
})
export class NotificationsMenuListItemComponent implements OnInit {
    @Input() notification!: Notification;

    @Output() removeNotification = new EventEmitter<Notification>();

    @Output() notificationRead = new EventEmitter<Notification>();

    errorMessage?: string;

    expandable: boolean = false;

    expanded: boolean = false;

    bulletColor: string = 'bullet-green';

    canRetry: boolean = false;

    retrying: boolean = false;

    constructor(private notificationsService: NotificationsService) {}

    ngOnInit(): void {
        this.expandable = !!this.notification.message && this.notification.message.length > 30;
        this.bulletColor = this.getBulletColor();
        this.canRetry = this.getCanRetry();
    }

    deleteNotification(): void {
        this.notificationsService.deleteNotification(this.notification).subscribe({
            next: () => {
                this.removeNotification.emit(this.notification);
            },
            error: (rsp) => {
                this.errorMessage = rsp.error;
            },
        });
    }

    getBulletColor(): string {
        if (this.notification.read) {
            return 'bullet-gray-70';
        }

        if (TODO_NOTIFICATION_TYPES.has(this.notification.type)) {
            return 'bullet-orange';
        }
        return 'bullet-green';
    }

    markNotificationRead(): void {
        if (!this.notification.read) {
            this.notificationsService.markNotificationAsRead(this.notification).subscribe({
                next: () => {
                    this.notification.read = true;
                    this.bulletColor = this.getBulletColor();
                    this.notificationRead.emit(this.notification);
                },
                error: (rsp) => {
                    this.errorMessage = rsp.error;
                },
            });
        }
    }

    retryReportGeneration(event: Event): void {
        // Keep the click from also reaching the row's mark-as-read handler.
        event.stopPropagation();
        const jobId = this.notification.data?.job_id;
        if (!jobId || this.retrying) {
            return;
        }
        this.retrying = true;
        this.errorMessage = undefined;
        this.notificationsService.retryReportGeneration(jobId).subscribe({
            next: () => {
                // The retried run posts its own card, so this one is stale. Drop it from the list even if the
                // delete fails (e.g. already removed in another tab), so it isn't left stuck on "Retrying…".
                const removeCard = (): void => this.removeNotification.emit(this.notification);
                this.notificationsService
                    .deleteNotification(this.notification)
                    .subscribe({ next: removeCard, error: removeCard });
            },
            error: (rsp) => {
                this.retrying = false;
                if (rsp.status === 404 || rsp.error?.error_code === 'report_unavailable') {
                    // The saved report was deleted or is no longer shared: retrying again can't succeed.
                    this.canRetry = false;
                    this.errorMessage = 'This report can no longer be retried.';
                    return;
                }
                // Anything else, e.g. a signed-out 403 (plain text), can succeed later, so Retry stays.
                this.errorMessage =
                    (typeof rsp.error === 'string' && rsp.error) ||
                    rsp.error?.error_message ||
                    'Couldn’t retry this report. Please try again.';
            },
        });
    }

    private getCanRetry(): boolean {
        if (!this.notification.data?.job_id) {
            return false;
        }
        return (
            this.notification.type === 'report_generation_failed' ||
            (this.notification.type === 'report_generation_complete' && this.notification.data.failed_count > 0)
        );
    }

    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }
}
